import { useState } from 'react';
import { useSceneSelection } from '@gorampe/viz-3d';
import { useTranslation } from '@/lib/i18n';
import type { BOMNode, BOMGroup, BOMLeaf } from '@gorampe/viz-3d';

interface Props {
  tree: BOMNode[];
}

export function BOMTree({ tree }: Props) {
  const { t } = useTranslation();
  return (
    <div className="w-full" role="tree" aria-label={t('bom.tree.aria')}>
      <table className="w-full text-sm border-collapse">
        <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-600">
          <tr>
            <th className="px-2 py-1.5 w-10" aria-hidden="true"></th>
            <th className="px-2 py-1.5">SKU</th>
            <th className="px-2 py-1.5">Description</th>
            <th className="px-2 py-1.5 text-right">Qté</th>
            <th className="px-2 py-1.5 text-right hidden sm:table-cell">Poids unit.</th>
            <th className="px-2 py-1.5 text-right hidden md:table-cell">Poids total</th>
            <th className="px-2 py-1.5 hidden lg:table-cell">Matériau</th>
            <th className="px-2 py-1.5 text-right hidden md:table-cell">Prix unit.</th>
          </tr>
        </thead>
        <tbody>
          {tree.map((node, i) => (
            <BOMNodeRows key={i} node={node} depth={0} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BOMNodeRows({ node, depth }: { node: BOMNode; depth: number }) {
  if (node.kind === 'group') return <BOMGroupRows group={node} depth={depth} />;
  return <BOMLeafRow leaf={node} depth={depth} />;
}

function BOMGroupRows({ group, depth }: { group: BOMGroup; depth: number }) {
  const [open, setOpen] = useState(true);
  return (
    <>
      <tr role="treeitem" aria-expanded={open} aria-selected={false} className="bg-slate-50/60">
        <td colSpan={8} className="px-0 py-0">
          <button
            onClick={() => setOpen(!open)}
            style={{ paddingLeft: 8 + depth * 12 }}
            className="w-full text-left px-2 py-1.5 font-semibold text-[var(--ink,#0F1E33)] hover:bg-slate-100 transition-colors"
          >
            <span aria-hidden="true">{open ? '▼' : '▶'}</span> {group.label}{' '}
            <span className="opacity-60 font-normal">({group.children.length})</span>
          </button>
        </td>
      </tr>
      {open && group.children.map((c, i) => <BOMNodeRows key={i} node={c} depth={depth + 1} />)}
    </>
  );
}

function BOMLeafRow({ leaf, depth }: { leaf: BOMLeaf; depth: number }) {
  const setHovered = useSceneSelection((s) => s.setHovered);
  const setSelected = useSceneSelection((s) => s.setSelected);
  const selectedRef = useSceneSelection((s) => s.selectedPartRef);
  const hoveredRef = useSceneSelection((s) => s.hoveredPartRef);
  const isActive = selectedRef === leaf.refId || hoveredRef === leaf.refId;
  const totalWeight = leaf.weightLbs != null && leaf.qty != null ? leaf.weightLbs * leaf.qty : null;

  return (
    <tr
      role="treeitem"
      tabIndex={0}
      aria-selected={selectedRef === leaf.refId}
      onMouseEnter={() => setHovered(leaf.refId)}
      onMouseLeave={() => setHovered(null)}
      onClick={() => setSelected(leaf.refId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') setSelected(leaf.refId);
      }}
      className={`cursor-pointer text-xs border-b border-slate-100 last:border-b-0 ${
        isActive ? 'bg-cyan-50' : 'hover:bg-slate-50'
      }`}
    >
      <td className="px-2 py-1.5">
        {leaf.imageUrl ? (
          <img
            src={leaf.imageUrl}
            alt={leaf.partNumber}
            className="h-8 w-8 object-contain rounded bg-slate-50"
            loading="lazy"
          />
        ) : (
          <div className="h-8 w-8 rounded bg-slate-100" aria-hidden="true" />
        )}
      </td>
      <td className="px-2 py-1.5 font-mono text-[11px]" style={{ paddingLeft: 8 + depth * 12 }}>
        {leaf.partNumber}
        {leaf.revision != null && (
          <span className="ml-1 text-[10px] text-slate-400">R{leaf.revision}</span>
        )}
      </td>
      <td className="px-2 py-1.5 text-slate-700">
        {leaf.label}
        {leaf.cutOnSite && (
          <span className="ml-1.5 text-[10px] text-amber-600" title="Cut on site">
            ✂ {leaf.stockLengthIn}"
          </span>
        )}
      </td>
      <td className="px-2 py-1.5 text-right font-mono tabular-nums">×{leaf.qty}</td>
      <td className="px-2 py-1.5 text-right font-mono tabular-nums hidden sm:table-cell text-slate-600">
        {leaf.weightLbs != null ? `${leaf.weightLbs.toFixed(1)} lbs` : '—'}
      </td>
      <td className="px-2 py-1.5 text-right font-mono tabular-nums hidden md:table-cell text-slate-600">
        {totalWeight != null ? `${totalWeight.toFixed(1)} lbs` : '—'}
      </td>
      <td className="px-2 py-1.5 hidden lg:table-cell text-[11px] text-slate-600">
        {leaf.material ?? '—'}
      </td>
      <td className="px-2 py-1.5 text-right font-mono tabular-nums hidden md:table-cell text-slate-700">
        {leaf.priceCadUnit != null ? `${leaf.priceCadUnit.toFixed(2)} $` : '—'}
      </td>
    </tr>
  );
}
